import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '../Icon';
import Tooltip from '../Tooltip';

type StyleMap = {
  open: {
    left: { marginLeft: string };
    right: { marginRight: string };
  };
  closed: {
    left: { marginLeft: string };
    right: { marginRight: string };
  };
};

const borderSize = 4;
const collapsedWidth = 85;
const closeIconWidth = 30;
const gridHorizontalPadding = 10;
const tabSpacerWidth = 2;

const baseClasses =
  'transition-all duration-300 ease-in-out bg-gray-100 border-gray-100 justify-start box-content flex flex-col';

const classesMap = {
  open: {
    left: `mr-1`,
    right: `ml-1`,
  },
  closed: {
    left: `mr-2 items-end`,
    right: `ml-2 items-start`,
  },
};

const openStateIconName = {
  left: 'side-panel-close-left',
  right: 'side-panel-close-right',
};

const getTabWidth = (numTabs: number) => (numTabs < 3 ? 140 : 100);

const getGridWidth = (numTabs: number, gridAvailableWidth: number) => {
  const spacersWidth = (numTabs - 1) * tabSpacerWidth;
  const tabsWidth = getTabWidth(numTabs) * numTabs;
  return gridAvailableWidth > tabsWidth + spacersWidth
    ? tabsWidth + spacersWidth
    : gridAvailableWidth;
};

const getNumGridColumns = (numTabs: number, gridWidth: number) => {
  if (numTabs === 1) return 1;
  // Start by calculating the number of tabs assuming each tab was accompanied by a spacer.
  const tabWidth = getTabWidth(numTabs);
  const numTabsWithOneSpacerEach = Math.floor(gridWidth / (tabWidth + tabSpacerWidth));
  // But there is always one less spacer than tabs, so now check if an extra tab with one less spacer fits.
  return (numTabsWithOneSpacerEach + 1) * tabWidth + numTabsWithOneSpacerEach * tabSpacerWidth <=
    gridWidth
    ? numTabsWithOneSpacerEach + 1
    : numTabsWithOneSpacerEach;
};

const getGridStyle = (
  side: string,
  numTabs: number = 0,
  gridWidth: number,
  expandedWidth: number
): CSSProperties => {
  const relativePosition = Math.max(0, Math.floor(expandedWidth - gridWidth) / 2 - closeIconWidth);
  return {
    position: 'relative',
    ...(side === 'left' ? { right: `${relativePosition}px` } : { left: `${relativePosition}px` }),
    width: `${gridWidth}px`,
  };
};

const getTabClassNames = (
  numColumns: number,
  numTabs: number,
  tabIndex: number,
  isActiveTab: boolean,
  isTabDisabled: boolean
) =>
  classnames('h-[80px] cursor-pointer text-black bg-[#d9d9d9]', {
    'hover:text-primary-active': !isActiveTab && !isTabDisabled,
  });

const getTabStyle = (numTabs: number) => {
  return {
    width: `${getTabWidth(numTabs)}px`,
  };
};

const getTabIconClassNames = (numTabs: number, isActiveTab: boolean) => {
  return classnames('h-full w-full flex items-center justify-center', {
    'bg-customblue-40': isActiveTab,
    rounded: isActiveTab,
  });
};

const createStyleMap = (
  expandedWidth: number,
  borderSize: number,
  collapsedWidth: number
): StyleMap => {
  const collapsedHideWidth = expandedWidth - collapsedWidth - borderSize;
  return {
    open: {
      left: { marginLeft: '0px' },
      right: { marginRight: '0px' },
    },
    closed: {
      left: { marginLeft: `-${collapsedHideWidth}px` },
      right: { marginRight: `-${collapsedHideWidth}px` },
    },
  };
};

const getToolTipContent = (label: string, disabled: boolean) => {
  return (
    <>
      <div>{label}</div>
      {disabled && <div className="text-black">{'Not available based on current context'}</div>}
    </>
  );
};

const createBaseStyle = (expandedWidth: number) => {
  return {
    maxWidth: `${expandedWidth}px`,
    width: `${expandedWidth}px`,
    // To align the top of the side panel with the top of the viewport grid, use position relative and offset the
    // top by the same top offset as the viewport grid. Also adjust the height so that there is no overflow.
    position: 'relative',
    height: '99.8%',
  };
};

const SidePanel = ({
  side,
  className,
  activeTabIndex: activeTabIndexProp,
  tabs,
  onOpen,
  expandedWidth = 280,
  onActiveTabIndexChange,
}) => {
  const { t } = useTranslation('SidePanel');
  const [panelOpen, setPanelOpen] = useState(activeTabIndexProp !== null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const styleMap = createStyleMap(expandedWidth, borderSize, collapsedWidth);
  const baseStyle = createBaseStyle(expandedWidth);
  const gridAvailableWidth = expandedWidth - closeIconWidth - gridHorizontalPadding;
  const gridWidth = getGridWidth(tabs.length, gridAvailableWidth);
  const openStatus = panelOpen ? 'open' : 'closed';
  const style = Object.assign({}, styleMap[openStatus][side], baseStyle);

  const updatePanelOpen = useCallback(
    (panelOpen: boolean) => {
      setPanelOpen(panelOpen);
      if (panelOpen && onOpen) onOpen();
    },
    [onOpen]
  );

  const updateActiveTabIndex = useCallback(
    (activeTabIndex: number) => {
      if (activeTabIndex === null) {
        updatePanelOpen(false);
        return;
      }
      setActiveTabIndex(activeTabIndex);
      updatePanelOpen(true);
      if (onActiveTabIndexChange) onActiveTabIndexChange({ activeTabIndex });
    },
    [onActiveTabIndexChange, updatePanelOpen]
  );

  useEffect(() => {
    updateActiveTabIndex(activeTabIndexProp);
  }, [activeTabIndexProp, updateActiveTabIndex]);

  const getCloseIcon = () => (
    <div
      className={classnames(
        'flex h-[28px] cursor-pointer items-center justify-center',
        side === 'left' ? 'order-last' : 'order-first'
      )}
      style={{ width: `${closeIconWidth}px` }}
      onClick={() => updatePanelOpen(!panelOpen)}
      data-cy={`side-panel-header-${side}`}
    >
      <Icon
        name={openStateIconName[side]}
        className="text-black"
      />
    </div>
  );

  const getCloseStateComponent = () => {
    const indexedTabs = Array.isArray(tabs)
      ? tabs.map((tab, index) => ({ tab, originalIndex: index }))
      : [{ tab: tabs, originalIndex: 0 }];

    const reordered = indexedTabs.length > 1 ? [...indexedTabs.slice(1), indexedTabs[0]] : indexedTabs;

    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ backgroundColor: '#f7fafc', width: '95px' }}
      >
        <div
          className="flex flex-col items-center space-y-4 pt-4"
          style={{
            backgroundColor: '#d9d9d9',
            width: '150px',
            maxWidth: '150px',
            padding: '12px 0',
            boxSizing: 'border-box',
          }}
        >
          {reordered.map(({ tab: childComponent, originalIndex }) => (
            <div
              key={originalIndex}
              className="flex cursor-pointer select-none flex-col items-center"
              onClick={() => {
                if (!childComponent.disabled) updateActiveTabIndex(originalIndex);
              }}
              id={`${childComponent.name}-btn`}
              data-cy={`${childComponent.name}-btn`}
              style={{
                width: '100%',
              }}
            >
              <Icon
                name={childComponent.iconName}
                className={classnames({
                  'text-black': !childComponent.disabled,
                  'opacity-50': childComponent.disabled,
                })}
                style={{
                  width: '32px',
                  height: '32px',
                }}
              />
              <span
                className={classnames('mt-2 text-center text-xs text-black', {
                  'cursor-not-allowed opacity-50': childComponent.disabled,
                })}
                style={{ lineHeight: '1.2', whiteSpace: 'normal' }}
              >
                {childComponent.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getTabGridComponent = () => {
    const indexedTabs = tabs.map((tab, index) => ({ tab, originalIndex: index }));

    const reordered = indexedTabs.length > 1 ? [...indexedTabs.slice(1), indexedTabs[0]] : indexedTabs;

    return (
      <div
        className={classnames('flex h-full grow')}
        style={{ paddingTop: '30px', paddingLeft: '3px' }}
      >
        <div
          className="flex"
          style={getGridStyle(side, tabs.length, gridWidth, expandedWidth)}
        >
          {reordered.map(({ tab, originalIndex }) => {
            const isActive = originalIndex === activeTabIndex;
            const disabled = tab.disabled;

            const tabClass = classnames(
              'flex flex-col items-center justify-center px-2 py-1 cursor-pointer text-xs select-none transition-colors duration-200',
              {
                'bg-primary-light text-black': isActive,
                'bg-[#d9d9d9] hover:bg-primary-light hover:text-black': !isActive && !disabled,
                'bg-[#bfbfbf] text-gray-500 cursor-not-allowed': disabled,
              }
            );

            const iconClass = classnames('transition-all flex items-center justify-center', {
              'text-black': isActive,
              'opacity-50': disabled,
            });

            return (
              <div
                key={originalIndex}
                onClick={() => {
                  if (!disabled) updateActiveTabIndex(originalIndex);
                }}
                className={classnames(tabClass, 'h-[100px] w-[90px] flex-shrink-0')}
                data-cy={`${tab.name}-btn`}
              >
                <div
                  className={iconClass}
                  style={{ marginBottom: '8px' }}
                >
                  <Icon
                    name={tab.iconName}
                    className={classnames({ 'ohif-disabled': disabled })}
                    style={{ width: '28px', height: '28px' }}
                  />
                </div>
                <span className="break-words text-center text-xs">{tab.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getOneTabComponent = () => (
    <div
      className={classnames(
        'text-primary-active flex grow cursor-pointer select-none justify-center self-center text-[13px]'
      )}
      style={{
        ...(side === 'left'
          ? { marginLeft: `${closeIconWidth}px` }
          : { marginRight: `${closeIconWidth}px` }),
      }}
      data-cy={`${tabs[0].name}-btn`}
      onClick={() => updatePanelOpen(!panelOpen)}
    >
      <span>{tabs[0].label}</span>
    </div>
  );

  const getOpenStateComponent = () => (
    <div className="flex select-none bg-gray-100 pt-1.5 pb-[2px]">
      {getCloseIcon()}
      {tabs.length === 1 ? getOneTabComponent() : getTabGridComponent()}
    </div>
  );

  return (
    <div
      className={classnames(className, baseClasses, classesMap[openStatus][side])}
      style={style}
    >
      {panelOpen ? (
        <>
          {getOpenStateComponent()}
          <div style={{ paddingTop: '8px' }}>
            {tabs.map((tab, tabIndex) => {
              if (tabIndex === activeTabIndex) {
                return <tab.content key={tabIndex} />;
              }
              return null;
            })}
          </div>
        </>
      ) : (
        <React.Fragment>{getCloseStateComponent()}</React.Fragment>
      )}
    </div>
  );
};

SidePanel.defaultProps = {
  defaultComponentOpen: null,
  activeTabIndex: null,
};

SidePanel.propTypes = {
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  className: PropTypes.string,
  activeTabIndex: PropTypes.number,
  tabs: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        iconName: PropTypes.string.isRequired,
        iconLabel: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        content: PropTypes.func,
        disabled: PropTypes.bool,
      })
    ),
  ]),
  onOpen: PropTypes.func,
  onActiveTabIndexChange: PropTypes.func,
  expandedWidth: PropTypes.number,
};

export default SidePanel;
